import React, { useEffect, useState } from "react";
import { retrieveProfile } from "../../../api/Api";
import { Link, useNavigate } from "react-router-dom";

const MyProfile = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        retrieveProfile()
            .then((res) => {
                setUser(res);
                setLoading(false);
                console.log("DATA RECEIVED SUCCESSFULLY :", res);
            })
            .catch((err) => {
                setError("Failed to fetch profile data. Please try again later.");
                console.error(err);
                setLoading(false);
            });
    }, []);

    if (loading)
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-teal-500 mx-auto"></div>
                    <p className="mt-4 text-lg font-medium text-gray-700">
                        Loading profile...
                    </p>
                </div>
            </div>
        );

    if (error)
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
                <div className="bg-white p-6 rounded-xl shadow-md max-w-md w-full text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                        <svg
                            className="h-6 w-6 text-red-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>
                    <h3 className="mt-3 text-lg font-medium text-gray-900">
                        Error loading profile
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );

    if (!user) return null;

    return (
        <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Back button for mobile */}
                <button
                    onClick={() => navigate(-1)}
                    className="md:hidden mb-4 flex items-center text-teal-600 hover:text-teal-800"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-1"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fillRule="evenodd"
                            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                            clipRule="evenodd"
                        />
                    </svg>
                    Back
                </button>

                <div className="bg-white shadow-xl rounded-xl overflow-hidden">
                    {/* Header Section */}
                    <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-4 sm:p-6 text-white">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">
                                    {user.name || "Unknown Player"}
                                </h1>
                                <p className="text-teal-100 mt-1 text-sm sm:text-base">
                                    User Profile
                                </p>
                            </div>
                            <button
                                onClick={() => navigate("/")}
                                className="hidden md:flex items-center px-4 py-2 bg-white text-teal-600 font-medium rounded-lg hover:bg-teal-50 transition-colors duration-200 shadow-sm"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5 mr-2"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                Back
                            </button>
                        </div>
                    </div>

                    {/* Basic Info Section */}
                    <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <InfoCard label="Email" value={user.pupilEmail} icon="mail" />
                        <InfoCard label="Mobile" value={user.pupilMobile} icon="phone" />
                        <InfoCard label="Aadhar" value={user.aadhar} icon="id" />
                        <InfoCard label="PAN" value={user.pan} icon="id" />
                        <InfoCard label="Status" value={user.status} icon="status" />
                        <InfoCard
                            label="Organization"
                            value={user.category}
                            icon="office"
                        />
                    </div>

                    {/* Subscriptions Section */}
                    <div className="border-t border-gray-200 px-4 sm:px-6 py-4 sm:py-6">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 sm:mb-6">
                            Your Plans
                        </h2>
                        {/* ✅ Fixed plan mapping */}
                        <YourPlans user={user} />
                    </div>
                </div>
            </div>
        </div>
    );
};

const InfoCard = ({ label, value, icon }) => {
    const getIcon = () => {
        switch (icon) {
            case "mail":
                return (
                    <svg
                        className="h-5 w-5 text-teal-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                    </svg>
                );
            case "phone":
                return (
                    <svg
                        className="h-5 w-5 text-teal-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                    </svg>
                );
            case "user":
                return (
                    <svg
                        className="h-5 w-5 text-teal-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                    </svg>
                );
            case "status":
                return (
                    <svg
                        className="h-5 w-5 text-teal-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 01118 0z"
                        />
                    </svg>
                );
            case "id":
                return (
                    <svg
                        className="h-5 w-5 text-teal-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
                        />
                    </svg>
                );
            case "office":
                return (
                    <svg
                        className="h-5 w-5 text-teal-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                    </svg>
                );
            default:
                return null;
        }
    };

    return (
        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg flex items-start space-x-3">
            <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
            <div>
                <h3 className="text-xs sm:text-sm font-medium text-gray-500">{label}</h3>
                <p className="mt-1 text-sm sm:text-base font-semibold text-gray-900 break-all">
                    {value || "-"}
                </p>
            </div>
        </div>
    );
};

const YourPlans = ({ user }) => {
    const navigate = useNavigate();

    // Enhanced function to check if user has any data for a specific plan type
    const hasPlanData = (planData) => {
        if (!planData) return false;
        
        // Handle array data (multiple plans)
        if (Array.isArray(planData)) {
            return planData.length > 0 && planData.some(item => item !== null && item !== undefined);
        }
        
        // Handle object data (single plan)
        if (typeof planData === 'object') {
            return Object.keys(planData).length > 0;
        }
        
        return false;
    };

    // Function to get plan count
    const getPlanCount = (planData) => {
        if (!planData) return 0;
        
        if (Array.isArray(planData)) {
            return planData.filter(item => item !== null && item !== undefined).length;
        }
        
        // For gold plans which have a plans array inside the object
        if (typeof planData === 'object' && Array.isArray(planData.plans)) {
            return planData.plans.filter(item => item !== null && item !== undefined).length;
        }
        
        if (typeof planData === 'object' && Object.keys(planData).length > 0) {
            return 1;
        }
        
        return 0;
    };

    // ✅ CORRECTED: Fixed the state data structure to match what your individual pages expect
    const plans = [
        {
            title: "Gold Chit Plan",
            description: "Save monthly and collect gold at maturity.",
            path: "/UserGoldPlan",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 text-yellow-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zm0 0v-2m0 2v2m0 4v2m-6-6h12"
                    />
                </svg>
            ),
            dataKey: "plans",
            // ✅ CORRECTED: Pass the data structure that UserGoldPlan expects
            getStateData: (userData) => ({
                user: userData,
                planData: userData.plans // This matches what UserGoldPlan expects
            })
        },
        {
            title: "Money Chit Plan",
            description: "Contribute monthly and get lump sum returns.",
            path: "/moneyChitPlan",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 text-green-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zm0 0V6m0 2v2m0 4v2m-6-6h12"
                    />
                </svg>
            ),
            dataKey: "chitPlanDetails",
            // ✅ CORRECTED: Pass the data structure that MoneyChitPlans expects
            getStateData: (userData) => ({
                chitPlanDetails: userData.chitPlanDetails // This matches what MoneyChitPlans expects
            })
        },
        {
            title: "Personal Loan",
            description: "Apply for instant personal loans with flexible EMIs.",
            path: "/UserPersonalLoans",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 text-blue-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 9V7a5 5 0 00-10 0v2M5 9h14l1 12H4L5 9z"
                    />
                </svg>
            ),
            dataKey: "personalLoanData",
            // ✅ CORRECTED: Pass the data structure that UserPersonalLoans expects
            getStateData: (userData) => ({
                user: userData,
                personalLoanData: userData.personalLoanData // This matches what UserPersonalLoans expects
            })
        },
    ];

    // Debug: Check what data we actually have
    console.log("User data for plans:", {
        plans: user?.plans,
        chitPlanDetails: user?.chitPlanDetails,
        personalLoanData: user?.personalLoanData,
        hasPlans: hasPlanData(user?.plans),
        hasChitPlanDetails: hasPlanData(user?.chitPlanDetails),
        hasPersonalLoanData: hasPlanData(user?.personalLoanData)
    });

    const hasAnyPlan = plans.some(plan => hasPlanData(user[plan.dataKey]));

    const handlePlanClick = (plan) => {
        const planData = user[plan.dataKey];
        const hasData = hasPlanData(planData);
        
        if (hasData) {
            const stateData = plan.getStateData(user);
            console.log(`Navigating to ${plan.path} with data:`, stateData);
            navigate(plan.path, { state: stateData });
        }
    };

    return (
        <div className="py-8 px-4 bg-gray-50 rounded-lg">
            <h2 className="text-xl font-semibold text-center text-gray-800 mb-6">
                Your Plans
            </h2>
            
            {hasAnyPlan ? (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {plans.map((plan, index) => {
                        const planData = user[plan.dataKey];
                        const hasData = hasPlanData(planData);
                        const planCount = getPlanCount(planData);

                        return (
                            <div
                                key={index}
                                className={`bg-white shadow-md rounded-2xl p-6 text-center hover:shadow-lg transition ${
                                    !hasData ? 'opacity-60' : ''
                                }`}
                            >
                                <div className="flex justify-center">{plan.icon}</div>
                                <h3 className="mt-4 text-lg font-medium text-gray-900">
                                    {plan.title}
                                </h3>
                                <p className="mt-2 text-sm text-gray-500">{plan.description}</p>
                                
                                {/* Show plan count if user has data */}
                                {hasData && (
                                    <div className="mt-2">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                                            {planCount} active plan{planCount !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                )}

                                <button 
                                    onClick={() => handlePlanClick(plan)}
                                    className={`mt-4 px-4 py-2 rounded-lg ${
                                        hasData 
                                            ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                                    disabled={!hasData}
                                >
                                    {hasData ? 'View Details' : 'No Active Plans'}
                                </button>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center py-8">
                    <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-12 w-12 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No Active Plans
                    </h3>
                    <p className="text-gray-500 mb-4">
                        You don't have any active plans yet. Start by exploring our services.
                    </p>
                    <button
                        onClick={() => navigate("/services")}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700"
                    >
                        Explore Services
                    </button>
                </div>
            )}
        </div>
    );
};

export default MyProfile;